/**
 * ReferralUpsellPopup — post-login "Refer & earn ₹15,000" nudge.
 *
 * Show rules (all must be true):
 *   1. User is authenticated
 *   2. User's profile is "set up" — they've finished onboarding (profile_completion >= 50%)
 *   3. Impression count < MAX_IMPRESSIONS (default 10)
 *   4. User hasn't ticked the "Don't show this again" opt-out
 *   5. At least 30s since last shown (per-session throttle)
 *   6. Delay 5s after first dashboard land — never on first page paint
 *
 * The component is purely client-side state (localStorage). Optimistic UI:
 * impressions are bumped immediately so a slow API doesn't double-fire.
 *
 * Metrics: every show/click/dismiss is sent to /api/growth/track for funnel
 * analysis (`event=refer_popup_*`).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Gift, X, Sparkles, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_IMPRESSIONS = 10;
const SESSION_THROTTLE_MS = 30_000;
const INITIAL_DELAY_MS = 5_000;

// Paths where the popup should never auto-appear (would interrupt a
// task-focused flow like multi-step forms or onboarding)
const SUPPRESS_PATH_PREFIXES = [
  '/dashboard/provider',  // Phase 60 Session 2 — provider apply / inbox
  '/services/sub/',       // service request forms
  '/dashboard/refer',     // already on the referral hub itself
  '/real-estate-exit',    // Phase 61 — deal room, submit form, lightbox, flyer
  '/login',
  '/register',
  '/onboard',
];

const KEY_IMPRESSIONS = 'ibclub_refer_popup_impressions';
const KEY_OPTED_OUT = 'ibclub_refer_popup_optout';
const KEY_LAST_SHOWN = 'ibclub_refer_popup_last_shown';
const KEY_CTA_CLICKED = 'ibclub_refer_popup_cta_clicked';

const trackEvent = (event, props = {}) => {
  // Best-effort — never blocks UI
  axios.post(`${API}/growth/track`,
    { event, ...props },
    { withCredentials: true, timeout: 3000 },
  ).catch(() => {});
};

const ReferralUpsellPopup = () => {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState(null);
  const [referData, setReferData] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const location = useLocation();
  const suppressed = SUPPRESS_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    let active = true;

    if (suppressed) return undefined;

    // Skip immediately if already opted out
    try {
      if (localStorage.getItem(KEY_OPTED_OUT) === '1') return undefined;
      const impressions = parseInt(localStorage.getItem(KEY_IMPRESSIONS) || '0', 10);
      if (impressions >= MAX_IMPRESSIONS) return undefined;
      const lastShown = parseInt(localStorage.getItem(KEY_LAST_SHOWN) || '0', 10);
      if (Date.now() - lastShown < SESSION_THROTTLE_MS) return undefined;
      // Hide forever if they've already engaged with the CTA at least once
      if (localStorage.getItem(KEY_CTA_CLICKED) === '1' && impressions >= 3) return undefined;
    } catch { /* localStorage blocked → just don't show */ return undefined; }

    // Probe auth + profile completeness — silent failure means no popup
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        if (!active) return;
        const u = r.data;
        if (!u?.user_id) return;
        // Don't show during onboarding — profile must be at least 50% done
        // Heuristic: must have membership_status set AND not 'incomplete'
        const status = (u.membership_status || '').toLowerCase();
        if (!status || status === 'incomplete' || status === 'pending_payment') return;
        setUser(u);
        // Fetch referral context so we can personalise the message
        return axios.get(`${API}/invite/me`, { withCredentials: true });
      })
      .then((r) => {
        if (!active || !r?.data) return;
        setReferData(r.data);
        // Schedule the show after the initial delay
        const t = setTimeout(() => {
          if (!active) return;
          setShow(true);
          try {
            const impressions = parseInt(localStorage.getItem(KEY_IMPRESSIONS) || '0', 10) + 1;
            localStorage.setItem(KEY_IMPRESSIONS, String(impressions));
            localStorage.setItem(KEY_LAST_SHOWN, String(Date.now()));
            trackEvent('refer_popup_impression', { impressions });
          } catch { /* ignore */ }
        }, INITIAL_DELAY_MS);
        return () => clearTimeout(t);
      })
      .catch(() => {});

    return () => { active = false; };
  }, [suppressed]);

  const handleClose = () => {
    if (dontShowAgain) {
      try { localStorage.setItem(KEY_OPTED_OUT, '1'); } catch { /* ignore */ }
      trackEvent('refer_popup_optout');
    } else {
      trackEvent('refer_popup_dismiss');
    }
    setShow(false);
  };

  const handleCTA = () => {
    try { localStorage.setItem(KEY_CTA_CLICKED, '1'); } catch { /* ignore */ }
    trackEvent('refer_popup_cta_click');
    setShow(false);
  };

  if (!show || !user || !referData || suppressed) return null;

  // Smart message variants
  const hasReferred = (referData.stats?.completed || 0) > 0;
  const accruedEarnings = (referData.stats?.earnings_completed_inr || 0)
                        + (referData.stats?.earnings_pending_inr || 0);

  // Pre-formatted INR for accrued
  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
  const maxReward = referData.reward_per_referral_inr || 15000;

  return createPortal(
    <div
      className="fixed inset-0 z-[95] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      data-testid="refer-popup"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="glass-card border border-[#d4af37]/40 max-w-md w-full p-6 sm:p-8 relative rounded-xl overflow-hidden">
        {/* Decorative gradient blob */}
        <div
          className="absolute -top-20 -right-20 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.25), transparent 70%)' }}
        />

        <button
          type="button"
          onClick={handleClose}
          data-testid="refer-popup-close"
          aria-label="Close"
          className="absolute top-3 right-3 text-slate-500 hover:text-white p-1 z-10"
        >
          <X size={16} />
        </button>

        <div className="relative">
          <div className="bg-[#d4af37] text-slate-950 rounded-xl p-3 inline-flex mb-4">
            <Gift size={22} />
          </div>

          {hasReferred ? (
            <>
              <p className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">Keep going</p>
              <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-white mb-2 leading-tight">
                You've already earned {fmt(accruedEarnings)}
              </h2>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                Your {referData.stats?.completed} successful referral{(referData.stats?.completed || 0) > 1 ? 's' : ''} put real money in your bank.
                Every additional Elite invite pays you another <span className="text-[#d4af37] font-semibold">{fmt(maxReward)}</span>.
              </p>
            </>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">A quick win</p>
              <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-white mb-2 leading-tight">
                Refer a friend, earn up to {fmt(maxReward)}
              </h2>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                Know a founder or investor who'd thrive at IB Club? Share your invite link —
                you earn ₹5,000 / ₹10,000 / ₹15,000 depending on what tier they pick.
              </p>
            </>
          )}

          {/* Mini benefits */}
          <ul className="space-y-2 mb-5 text-xs text-slate-300">
            <li className="flex items-center gap-2">
              <Sparkles size={11} className="text-[#d4af37] flex-shrink-0" /> 
              Real bank transfers — monthly via NEFT/RTGS
            </li>
            <li className="flex items-center gap-2">
              <Sparkles size={11} className="text-[#d4af37] flex-shrink-0" /> 
              Your friend gets a 3-day free trial
            </li>
            <li className="flex items-center gap-2">
              <Sparkles size={11} className="text-[#d4af37] flex-shrink-0" /> 
              No cap on how many you invite
            </li>
          </ul>

          <Link
            to="/dashboard/refer"
            onClick={handleCTA}
            data-testid="refer-popup-cta"
            className="block w-full bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] font-semibold text-sm uppercase tracking-wider px-5 py-3 text-center"
          >
            {hasReferred ? 'Open referral hub' : 'Get my invite link'} <ArrowRight size={14} className="inline ml-1" />
          </Link>

          {/* Opt-out */}
          <label className="flex items-center gap-2 mt-4 text-[11px] text-slate-500 cursor-pointer hover:text-slate-300 select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              data-testid="refer-popup-optout-checkbox"
              className="rounded border-white/15 bg-slate-950 text-[#d4af37] focus:ring-[#d4af37]/30"
            />
            Don't show this again
          </label>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ReferralUpsellPopup;
